import React, { useCallback, useMemo } from 'react';

import { FloatingPortal, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { ArrowUpDown, Grid2x2, Trash2 } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { useListNavigation } from '../../hooks/useListNavigation';
import { GlassWrapper } from './GlassWrapper';
import { MenuButton } from './MenuButton';

// Same shell and rows as the rail's context menu — GlassWrapper + MenuButton + list
// navigation. It anchors to a button rather than a click point, so it does its own
// floating setup instead of borrowing useContextMenu's virtual reference.
export function WidgetsBoardMenu({ anchorRef, open, onClose, onReorder, onRemove, onBrowse }) {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) onClose();
    },
    placement: 'bottom-end',
    middleware: [offset(8), flip({ fallbackPlacements: ['top-end'] }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    elements: { reference: anchorRef?.current || null },
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);
  const { unlocked } = useRegisterWithKeyMaster(open ? KEYCHAIN.POPOVER__WIDGETS_BOARD_MENU : null);

  const menuItems = useMemo(
    () => [
      { key: 'reorder', label: 'Reorder widgets', icon: ArrowUpDown, handler: onReorder },
      { key: 'remove', label: 'Remove widgets', icon: Trash2, handler: onRemove },
      { key: 'browse', label: 'Browse widgets', icon: Grid2x2, handler: onBrowse },
    ],
    [onBrowse, onRemove, onReorder],
  );

  const handleItemSelect = useCallback(
    function selectWidgetsBoardMenuItem(item) {
      onClose();
      item.handler();
    },
    [onClose],
  );

  const { selectedIndex, createMouseEnterHandler, onMouseLeave, onKeyDown } = useListNavigation({
    items: menuItems,
    onSelect: handleItemSelect,
    wrap: true,
    initialIndex: 0,
  });

  const handleEscapeHotkey = useCallback(
    function closeWidgetsBoardMenuOnEscape() {
      if (unlocked()) onClose();
    },
    [onClose, unlocked],
  );

  const handleNavigationHotkey = useCallback(
    function navigateWidgetsBoardMenu(event) {
      if (!unlocked()) return;
      onKeyDown({ event });
    },
    [onKeyDown, unlocked],
  );

  useHotkeys('esc', handleEscapeHotkey, { enabled: open, enableOnFormTags: true, preventDefault: true }, [handleEscapeHotkey]);
  useHotkeys('up,down,enter', handleNavigationHotkey, { enabled: open, enableOnFormTags: true, preventDefault: true }, [
    handleNavigationHotkey,
  ]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <GlassWrapper
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps({ role: 'menu' })}
        className="z-(--stack-context-menu) w-44 gap-0 whitespace-nowrap"
        onMouseLeave={onMouseLeave}
      >
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <MenuButton
              key={item.key}
              role="menuitem"
              isActive={selectedIndex === index}
              onClick={() => handleItemSelect(item)}
              onMouseMove={createMouseEnterHandler(index)}
            >
              <span className="flex items-center gap-2.5">
                <Icon size={12} aria-hidden="true" />
                {item.label}
              </span>
            </MenuButton>
          );
        })}
      </GlassWrapper>
    </FloatingPortal>
  );
}

export default WidgetsBoardMenu;
